import { Print } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    InputAdornment,
} from "@mui/material";
import classNames from "classnames";
import { useContext, useEffect, useMemo, useState } from "react";
import { PosContext } from "../../../shared/PosContext";
import { useUpdateDoctypeData } from "../../../shared/hooks/useDocTypesData";
import { usePrintOrder } from "../../../shared/hooks/useTablesData";
import { useStyles } from "./styles";
import { useParams } from "react-router-dom";
import { AppContext } from "../../../AppContext";

function PrintOrderModal({ open, onClose, orders, isTakeaway }) {
    const [orderData, setOrderData] = useState({});
    const [selectedDiscount, setSelectedDiscount] = useState(0);
    
    // New state for fixed discount amount
    const [fixedDiscountAmount, setFixedDiscountAmount] = useState(0);
    const [discountType, setDiscountType] = useState("percentage"); // "percentage" or "fixed"

    const { orderName } = useParams();

    const { tableOrders, selectedTable, refetchTablesData } =
        useContext(PosContext);

    const { restaurantSettings, serviceFee } = useContext(AppContext);
    const printOrder = usePrintOrder();

    const classes = useStyles();

    const saveDoc = useUpdateDoctypeData();

    const [preSelectedDiscount, setPreSelectedDiscount] = useState(false);

    const order = useMemo(() => {
        if (orders) {
            if (isTakeaway) {
                return orders.find((i) => i.name === orderName);
            }
            return Object.values(orders)[0];
        }
    }, [orders, isTakeaway, orderName]);

    const roundToNearest5WithMaxCap = (num) => {
        if (num > 20) {
            return 20;
        }
        return Math.round(num / 5) * 5;
    };

    useEffect(() => {
        if (orderData.total && order && order.discount > 0) {
            setSelectedDiscount(
                roundToNearest5WithMaxCap(
                    (1 - (orderData.total - order.discount) / orderData.total) *
                        100
                )
            );
            setPreSelectedDiscount(true);
        } else {
            setPreSelectedDiscount(false);
            setSelectedDiscount(0);
        }
    }, [order, orderData]);

    useEffect(() => {
        if (order) {
            const _orderData = {};
            let total = 0;

            order.entry_items.map((item) => {
                total += item.rate * item.qty;
            });
            _orderData.total = total;
            setOrderData(_orderData);
        }
    }, [order]);

    const totalAfterDiscount = useMemo(() => {
        if (order) {
            const _orderData = {};
            let total = 0;

            order.entry_items.map((item) => {
                total += item.rate * item.qty;
            });
            
            // Priority order: Fixed discount > Percentage discount
            if (discountType === "fixed" && fixedDiscountAmount > 0) {
                // Fixed amount discount calculation
                return Math.max(0, total - fixedDiscountAmount);
            } else if (selectedDiscount > 0) {
                // Percentage discount calculation
                return total - (total * selectedDiscount) / 100;
            }
            
            return total;
        }
    }, [selectedDiscount, orderData, order, discountType, fixedDiscountAmount]);

    const handlePrint = () => {
        if (order) {
            const settingsInvoicePrinter =
                restaurantSettings?.docs[0]?.invoice_printer;
            // Apply discount based on type
            if (discountType === "fixed" && fixedDiscountAmount > 0) {
                // Fixed amount discount
                order.amount = Math.max(0, orderData.total - fixedDiscountAmount);
                order.discount = fixedDiscountAmount;
                order.total = orderData.total; // Preserve original total
            } else if (selectedDiscount !== 0) {
                // Percentage discount
                order.amount =
                    orderData.total -
                    (orderData.total * selectedDiscount) / 100;
                order.discount = (orderData.total * selectedDiscount) / 100;
                order.total = orderData.total; // Preserve original total
            } else {
                // No discount applied
                order.amount = orderData.total;
                order.discount = 0;
                order.total = orderData.total;
            }

            if (preSelectedDiscount) {
                if (!settingsInvoicePrinter) {
                    const printUrl = `/api/method/frappe.utils.print_format.download_pdf?doctype=Table%20Order&name=${order.name}&&format=Order%20Account%20Test&no_letterhead=0&settings=%7B%7D&_lang=en`;
                    // const printUrl = `/api/method/frappe.utils.print_format.download_pdf?doctype=Table%20Order&name=${order.name}&&format=Order%20Account&no_letterhead=1&letterhead=Antico&settings=%7B%7D&_lang=en`;

                    // Create a hidden iframe to handle the PDF printing
                    const iframe = document.createElement("iframe");
                    iframe.style.display = "none";
                    iframe.src = printUrl;
                    document.body.appendChild(iframe);

                    // Wait for the iframe to load and then print
                    iframe.onload = () => {
                        try {
                            iframe.contentWindow.print();
                            saveDoc.mutate({
                                doctype: order.doctype,
                                name: order.name,
                                formData: { ...order, invoice_printed: 1 },
                            });
                        } catch (error) {
                            console.error("Error printing invoice:", error);
                        }
                        // Clean up the iframe after printing
                        // setTimeout(() => {
                        // document.body.removeChild(iframe);
                        // }, 1000);
                    };
                } else {
                    printOrder.mutate(
                        {
                            orderId: order.name,
                            doctype: "Table Order",
                            printFormat: "Order Account Test",
                        },
                        {
                            onSuccess: (data) => {
                                onClose();
                                saveDoc.mutate({
                                    doctype: order.doctype,
                                    name: order.name,
                                    formData: { ...order, invoice_printed: 1 },
                                });

                                // refetchTablesData();
                                console.log("printing");
                            },
                            onError: (e) => {
                                console.log("error printing", e);
                            },
                        }
                    );
                }
            } else {
                saveDoc.mutate(
                    {
                        doctype: order.doctype,
                        name: order.name,
                        formData: { ...order, invoice_printed: 1 },
                    },
                    {
                        onSuccess: () => {
                            refetchTablesData().then(() => {
                                onClose();
                                if (!settingsInvoicePrinter) {
                                    const printUrl = `/api/method/frappe.utils.print_format.download_pdf?doctype=Table%20Order&name=${order.name}&format=Order%20Account%20Test&no_letterhead=0&settings=%7B%7D&_lang=en`;
                                    // const printUrl = `/api/method/frappe.utils.print_format.download_pdf?doctype=Table%20Order&name=${order.name}&format=Order%20Account&no_letterhead=1&letterhead=Antico&settings=%7B%7D&_lang=en`;

                                    // Create a hidden iframe to handle the PDF printing
                                    const iframe =
                                        document.createElement("iframe");
                                    iframe.style.display = "none";
                                    iframe.src = printUrl;
                                    document.body.appendChild(iframe);

                                    // Wait for the iframe to load and then print
                                    iframe.onload = () => {
                                        try {
                                            iframe.contentWindow.print();
                                        } catch (error) {
                                            console.error(
                                                "Error printing invoice:",
                                                error
                                            );
                                        }
                                        // Clean up the iframe after printing
                                        // setTimeout(() => {
                                        // document.body.removeChild(iframe);
                                        // }, 1000);
                                    };
                                } else {
                                    printOrder.mutate(
                                        {
                                            orderId: order.name,
                                            doctype: "Table Order",
                                            printFormat: "Order Account Test",
                                        },
                                        {
                                            onSuccess: (data) => {
                                                refetchTablesData();
                                                console.log("printing");
                                            },
                                            onError: (e) => {
                                                console.log(
                                                    "error printing",
                                                    e
                                                );
                                            },
                                        }
                                    );
                                }
                            });
                        },
                    }
                );
            }
        }
    };

    // Handle fixed discount amount input change
    const handleFixedDiscountChange = (event) => {
        const value = event.target.value === "" ? 0 : parseFloat(event.target.value) || 0;
        setFixedDiscountAmount(value);
        
        if (value > 0) {
            // Switch to fixed discount mode and clear percentage discount
            setDiscountType("fixed");
            setSelectedDiscount(0);
        } else {
            // Switch back to percentage mode when fixed amount is 0 or empty
            setDiscountType("percentage");
        }
    };

    // Handle percentage discount button click
    const handlePercentageDiscountClick = (percentage) => {
        if (!preSelectedDiscount) {
            // Clear fixed discount amount when using percentage
            setFixedDiscountAmount(0);
            setDiscountType("percentage");
            setSelectedDiscount(percentage);
        }
    };

    const serviceFeeRate = serviceFee > 0 ? 1 + serviceFee / 100 : 1;
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Print Order</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <div className={classes.Discount}>
                        <div className="Discount__Title">Discount</div>
                        
                        {/* Fixed Amount Discount Input */}
                        <div style={{ marginBottom: "15px" }}>
                            <TextField
                                label="Fixed Discount Amount"
                                type="number"
                                value={fixedDiscountAmount || ""}
                                onChange={handleFixedDiscountChange}
                                variant="outlined"
                                size="small"
                                disabled={preSelectedDiscount}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            ₪
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    min: 0,
                                    step: 0.01,
                                }}
                                style={{ 
                                    width: "100%",
                                    backgroundColor: preSelectedDiscount ? "#f5f5f5" : "white"
                                }}
                                helperText={discountType === "fixed" && fixedDiscountAmount > 0 ? "Fixed discount applied" : "Enter amount to use fixed discount"}
                            />
                        </div>
                        
                        {/* Percentage Discount Buttons */}
                        <div
                            className={`Discount__Amounts ${
                                preSelectedDiscount || discountType === "fixed" ? "Disabled" : ""
                            }`}
                        >
                            {[0, 5, 10, 15, 20].map((entry) => {
                                return (
                                    <div
                                        key={entry}
                                        onClick={() =>
                                            preSelectedDiscount || discountType === "fixed"
                                                ? console.log(entry)
                                                : handlePercentageDiscountClick(entry)
                                        }
                                        className={classNames("Number", {
                                            IsSelected:
                                                discountType === "percentage" && selectedDiscount === entry,
                                        })}
                                        style={{
                                            opacity: discountType === "fixed" ? 0.5 : 1,
                                            cursor: discountType === "fixed" ? "not-allowed" : "pointer"
                                        }}
                                    >
                                        {entry}%
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className={classes.Totals}>
                        <div>
                            <span className="title">Total :</span>{" "}
                            <span className="amount">
                                {Math.round(
                                    orderData.total *
                                        (isTakeaway ? 1 : serviceFeeRate)
                                )}
                                ₪
                            </span>{" "}
                        </div>
                        <div>
                            <span className="title">Total After Discount:</span>{" "}
                            <span className="amount">
                                {Math.round(
                                    Number(
                                        totalAfterDiscount *
                                            (isTakeaway ? 1 : serviceFeeRate)
                                    )
                                )}
                                ₪
                            </span>{" "}
                        </div>
                    </div>
                </Stack>
            </DialogContent>
            <DialogActions>
                {" "}
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handlePrint}
                >
                    {" "}
                    <Print style={{ marginRight: "5px" }} /> Print
                </Button>{" "}
            </DialogActions>
        </Dialog>
    );
}

export default PrintOrderModal;
